import { useEffect, useMemo, useRef, useState } from "react";
import type { Lead } from "@/types/lead";
import { extractTranscriptCueLines, parseTranscriptLineToFollowUpDraft } from "@/lib/lead/transcriptFollowUpCue";
import { followUpFromLocalDateTimeInput, followUpToLocalDateTimeInput } from "@/lib/lead/followUpDateTimeLocal";
import { compactInputClass, ghostBtnClass, insetSurfaceCardClass, salesIndigoOutlineBtnClass, salesIndigoSurfaceCardClass } from "@/components/common/surfaceClasses";
import { TranscriptCueList } from "./TranscriptCueList";
import { useGoogleCalendarUpcoming } from "@/hooks/useGoogleCalendarUpcoming";

export function CustomerCalendarCard(props: {
  lead: Lead;
  onOpenSalesConsole?: () => void;
  createFollowUp: (leadId: string, draft: { next_action: string; due_date: string; reason: string }) => void;
}) {
  const [cardOpen, setCardOpen] = useState(true);
  const [activeCue, setActiveCue] = useState<string | null>(null);
  const [draftAction, setDraftAction] = useState("");
  const [draftDueLocal, setDraftDueLocal] = useState("");
  const [draftReason, setDraftReason] = useState("");

  const openFollowUps = useMemo(
    () => (props.lead.follow_ups ?? []).filter((f) => f.status === "open").sort((a, b) => Date.parse(a.due_date) - Date.parse(b.due_date)),
    [props.lead.follow_ups]
  );
  const cues = useMemo(() => extractTranscriptCueLines(props.lead.transcript || ""), [props.lead.transcript]);
  const gcal = useGoogleCalendarUpcoming(true);

  const prevCueSigRef = useRef("");
  useEffect(() => {
    const sig = cues.join("\u0001");
    if (cues.length > 0 && sig !== prevCueSigRef.current) {
      prevCueSigRef.current = sig;
      setCardOpen(true);
    }
    if (cues.length === 0) prevCueSigRef.current = "";
  }, [cues]);

  function openCueEditor(line: string) {
    const d = parseTranscriptLineToFollowUpDraft(line);
    if (!d) return;
    setActiveCue(line);
    setDraftAction(d.next_action);
    setDraftDueLocal(followUpToLocalDateTimeInput(d.due_date));
    setDraftReason(d.reason);
  }

  function cancelCueEditor() {
    setActiveCue(null);
    setDraftAction("");
    setDraftDueLocal("");
    setDraftReason("");
  }

  function saveCueEditor() {
    if (!draftAction.trim() || !draftDueLocal) return;
    props.createFollowUp(props.lead.id, {
      next_action: draftAction.trim(),
      due_date: followUpFromLocalDateTimeInput(draftDueLocal),
      reason: draftReason.trim() || "From transcript cue",
    });
    cancelCueEditor();
  }

  return (
    <div className="mt-3 rounded-2xl border border-indigo-200 bg-indigo-50/40 text-left overflow-hidden dark:border-indigo-800/70 dark:bg-indigo-950/25">
      <button
        type="button"
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left hover:bg-indigo-100/50 dark:hover:bg-indigo-950/50"
        aria-expanded={cardOpen}
        onClick={() => setCardOpen((v) => !v)}
      >
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-indigo-900 dark:text-indigo-200">Customer Calendar</h4>
          <p className="text-[10px] text-indigo-800/80 dark:text-indigo-300/80">
            Transcript cues · open events · Sales Console for bulk edits
            {gcal.linked && gcal.eventsTodayCount > 0 ? (
              <span className="ml-1 inline-flex rounded-full bg-emerald-100 text-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-200 px-1.5 py-0.5 font-semibold">
                {gcal.eventsTodayCount} in Google today
              </span>
            ) : null}
          </p>
        </div>
        <span className="shrink-0 text-xs text-indigo-700 dark:text-indigo-300">{cardOpen ? "▲" : "▼"}</span>
      </button>

      {cardOpen ? (
        <div className="px-4 pb-3 space-y-3 border-t border-indigo-100 dark:border-indigo-900/60">
          <div className="flex flex-wrap items-start justify-between gap-2 pt-2">
            <p className="text-[11px] text-indigo-800/90 dark:text-indigo-300/90 flex-1 min-w-0">
              Expand a cue to dismiss it or set up a follow-up — or use Sales Console for bulk edits.
            </p>
            <button
              type="button"
              className={`shrink-0 ${salesIndigoOutlineBtnClass} px-2.5 py-1 font-semibold`}
              onClick={() => props.onOpenSalesConsole?.()}
            >
              Open Sales Console
            </button>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <div className={`${salesIndigoSurfaceCardClass} p-2`}>
              <div className="text-[11px] font-semibold text-indigo-900 dark:text-indigo-200">Open events</div>
              {openFollowUps.length ? (
                <ul className="mt-1 space-y-1 text-xs text-gray-700 dark:text-zinc-300">
                  {openFollowUps.slice(0, 4).map((f) => (
                    <li key={f.id}>
                      <span className="font-medium">{f.next_action}</span>
                      <span className="text-gray-500 dark:text-zinc-500"> · {new Date(f.due_date).toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">No open events on this lead yet.</p>
              )}
            </div>
            <div className={`${salesIndigoSurfaceCardClass} p-2`}>
              <div className="text-[11px] font-semibold text-indigo-900 dark:text-indigo-200">Transcript event cues</div>
              <TranscriptCueList cues={cues} onUseForFollowUp={openCueEditor} />
            </div>
          </div>

          {activeCue ? (
            <div className={`${insetSurfaceCardClass} border-indigo-200 space-y-2 dark:border-indigo-800/60`}>
              <div className="text-[11px] font-semibold text-indigo-900 dark:text-indigo-200">Confirm event from cue</div>
              <p className="text-[10px] text-gray-600 dark:text-zinc-400 italic line-clamp-2">{activeCue}</p>
              <input
                className={compactInputClass}
                value={draftAction}
                onChange={(e) => setDraftAction(e.target.value)}
                placeholder="Next action"
              />
              <input
                type="datetime-local"
                className={compactInputClass}
                value={draftDueLocal}
                onChange={(e) => setDraftDueLocal(e.target.value)}
              />
              <textarea
                className={`${compactInputClass} min-h-[48px]`}
                value={draftReason}
                onChange={(e) => setDraftReason(e.target.value)}
                placeholder="Reason / context"
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={!draftAction.trim() || !draftDueLocal}
                  className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                  onClick={saveCueEditor}
                >
                  Save as official event
                </button>
                <button type="button" className={`${ghostBtnClass} px-3 py-1.5 text-xs`} onClick={cancelCueEditor}>
                  Cancel
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
